import React, { useState, useEffect } from 'react';
import CategoriesTab from '../Role-based/CategoriesTab';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function AddCategory() {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddEditPopup, setShowAddEditPopup] = useState(false);
  const [currentEditingCategory, setCurrentEditingCategory] = useState(null);
  const [showEditConfirmPopup, setShowEditConfirmPopup] = useState(false);
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    fetch(`${BQ_BASE_URL}/bq/api/categories/`)
      .then((res) => res.json())
      .then((data) => setCategories(data))
      .catch((error) => console.error('Error fetching categories:', error));
  }, []);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const handleAddCategory = () => {
    setCurrentEditingCategory(null);
    setCategoryName('');
    setCategoryDescription('');
    setShowAddEditPopup(true);
  };

  const handleSaveCategory = () => {
    const newCategory = {
      name: categoryName,
      description: categoryDescription,
    };

    fetch(`${BQ_BASE_URL}/bq/api/categories/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(newCategory),
    })
      .then((response) => response.json())
      .then((data) => {
        setCategories((prevCategories) => [...prevCategories, data]);
        setShowAddEditPopup(false);
        setCategoryName('');
        setCategoryDescription('');
        toast.success('Category added successfully!');
      })
      .catch((error) => {
        console.error('Error adding category:', error);
        toast.error('Failed to add category.');
      });
  };

  const handleCancelAddEdit = () => {
    setShowAddEditPopup(false);
    setCategoryName('');
    setCategoryDescription('');
  };

  const handleEditCategory = (category) => {
    setCurrentEditingCategory(category);
    setCategoryName(category.name);
    setCategoryDescription(category.description);
    setShowAddEditPopup(true);
  };

  const handleDeleteCategory = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    setCategoryToDelete(category);
    setShowDeleteConfirmPopup(true);
  };

  const handleConfirmDelete = () => {
    fetch(`${BQ_BASE_URL}/bq/api/categories/${categoryToDelete.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        setCategories((prevCategories) =>
          prevCategories.filter((category) => category.id !== categoryToDelete.id)
        );
        setShowDeleteConfirmPopup(false);
        toast.success('Category deleted successfully!');
      })
      .catch((error) => {
        console.error('Error deleting category:', error);
        toast.error('Failed to delete category.');
      });
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmPopup(false);
    setCategoryToDelete(null);
  };

  const handleUpdateCategory = () => {
    setShowEditConfirmPopup(true);
  };

  const handleConfirmEdit = () => {
    const updatedCategory = {
      name: categoryName,
      description: categoryDescription,
    };

    fetch(`${BQ_BASE_URL}/bq/api/categories/${currentEditingCategory.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(updatedCategory),
    })
      .then((response) => response.json())
      .then((data) => {
        setCategories((prevCategories) =>
          prevCategories.map((category) => (category.id === data.id ? data : category))
        );
        setShowAddEditPopup(false);
        setShowEditConfirmPopup(false);
        setCurrentEditingCategory(null);
        setCategoryName('');
        setCategoryDescription('');
        toast.success('Category updated successfully!');
      })
      .catch((error) => {
        console.error('Error updating category:', error);
        toast.error('Failed to update category.');
      });
  };

  const handleCancelEdit = () => {
    setShowEditConfirmPopup(false);
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="mb-10 p-4">
      <ToastContainer />

      <h1 className="text-lg font-bold mt-2 mb-4 text-black">Create or Edit Category</h1>

      <div className="flex items-center gap-4 mb-10">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search Categories..."
          className="border rounded-lg py-2 px-4 w-1/3"
        />
        <button
          onClick={handleAddCategory}
          className="bg-black text-white px-4 py-2 rounded-lg hover:text-white"
        >
          Add Category
        </button>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4">Select Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {filteredCategories.map((category) => (
            <div
              key={category.id}
              className={`p-4 border rounded-lg cursor-pointer transition-all shadow-md hover:shadow-lg ${
                selectedCategories.includes(category.id) ? ' text-white' : 'bg-gray-100'
              }`}
              onClick={() => handleCategoryChange(category.id)}
            >
              <div className="flex justify-between items-center">
                <div className="font-bold">{category.name}</div>
                <div className="flex gap-2">
                  <FaEdit
                    className="h-5 w-5 text-blue-500 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCategory(category);
                    }}
                  />
                  <FaTrash
                    className="h-5 w-5 text-red-500 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCategory(category.id);
                    }}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">{category.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Category Popup */}
      {showAddEditPopup && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h2 className="text-2xl font-bold mb-4">
              {currentEditingCategory ? 'Edit Category' : 'Add New Category'}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Category Name</label>
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter Category Name"
                className="border rounded-lg py-2 px-4 w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Category Description</label>
              <textarea
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                placeholder="Enter Category Description"
                className="border rounded-lg py-2 px-4 w-full"
              />
            </div>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancelAddEdit}
                className="bg-gray-500 text-white hover:bg-gray-600 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={currentEditingCategory ? handleUpdateCategory : handleSaveCategory}
                className="bg-black hover:bg-black text-white px-4 py-2 rounded-lg"
              >
                {currentEditingCategory ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Confirmation Popup */}
      {showEditConfirmPopup && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h2 className="text-2xl font-bold mb-4 text-yellow-600">Confirm Changes</h2>
            <p className="mb-4">
              You are about to update the category "<strong>{currentEditingCategory?.name}</strong>
              ".
            </p>
            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <p className="font-semibold">Old values:</p>
              <p>Name: {currentEditingCategory?.name}</p>
              <p>Description: {currentEditingCategory?.description}</p>

              <p className="font-semibold mt-2">New values:</p>
              <p>Name: {categoryName}</p>
              <p>Description: {categoryDescription}</p>
            </div>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancelEdit}
                className="bg-gray-500 text-white hover:bg-gray-600 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmEdit}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
              >
                Confirm Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h2 className="text-2xl font-bold mb-4 text-red-600">Confirm Deletion</h2>
            <div className="bg-red-50 p-4 rounded-lg mb-4">
              <p className="mb-4">You are about to permanently delete the category:</p>
              <p className="font-bold text-lg">{categoryToDelete?.name}</p>
              {categoryToDelete?.description && (
                <p className="mt-2">{categoryToDelete.description}</p>
              )}
              <p className="mt-4 font-semibold text-red-600">This action cannot be undone!</p>
            </div>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancelDelete}
                className="bg-gray-500 text-white hover:bg-gray-600 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddCategory;
